import { objectify, stringify } from '#library/serialize.library.js';
import { CONTEXT, getContext } from '#library/utility.library.js';
import { isDefined, isUndefined, isString, isCallable, isNumber } from '#library/assertion.library.js';
import { BoundedCache } from '#library/cache.class.js';
const context = getContext();
/** Creates an in-memory Storage object fallback for environments without native Storage support */
export const createMemoryStorage = (maxSize = 1000, ttl = Infinity) => {
    const map = new BoundedCache(maxSize, ttl);
    return {
        get length() { return map.size; },
        clear: () => map.clear(),
        getItem: (key) => map.get(key) ?? null,
        key: (index) => Array.from(map.keys())[index] ?? null,
        removeItem: (key) => { map.delete(key); },
        setItem: (key, value) => { map.set(key, String(value)); },
    };
};
const memoryStores = new Map();
/**
 * Retrieves or creates a named in-memory Storage instance for environments without native Storage support.
 *
 * @param name - The identifier for the memory storage instance
 * @returns A Storage-compatible object backed by in-memory Map
 */
export const getMemoryStorage = (name) => {
    let store = memoryStores.get(name);
    if (!store) {
        store = createMemoryStorage();
        memoryStores.set(name, store);
    }
    return store;
};
/** Safely attempt to retrieve a Storage object from globalThis or fallback to memory storage */
export const getSafeStorage = (name = 'localStorage') => {
    try {
        const target = globalThis?.[name];
        if (target &&
            isCallable(target.getItem) &&
            isCallable(target.setItem) &&
            isCallable(target.removeItem) &&
            isCallable(target.clear) &&
            isCallable(target.key) &&
            isNumber(target.length))
            return target;
    }
    catch {
        // Ignore SecurityError / ReferenceError in restricted sandboxes
    }
    return getMemoryStorage(name);
};
let storage = context.type === CONTEXT.Browser
    ? getSafeStorage()
    : createMemoryStorage();
export const nodeStorage = new BoundedCache(1000, Infinity);
/**
 * Selects the active browser storage mechanism (localStorage or sessionStorage).
 *
 * @param store - The storage type to use (default: 'local')
 * @returns The selected Storage object
 */
export function selStorage(store = 'local') {
    const name = (store + 'Storage');
    return storage = getSafeStorage(name);
}
/**
 * Helper to safely retrieve environment variable values without static AST process.env MemberExpressions.
 * Uses dynamic reflection to avoid bundler optimizations and static analysis.
 *
 * @param key - The environment variable name to retrieve
 * @returns The environment variable value, or undefined if not available
 * @internal
 */
const getEnvVar = (key) => {
    try {
        const proc = Reflect.get(context.global, 'process');
        if (!proc || typeof proc !== 'object')
            return undefined;
        const env = Reflect.get(proc, 'env');
        if (!env || typeof env !== 'object')
            return undefined;
        return Reflect.get(env, key);
    }
    catch {
        return undefined;
    }
};
export function getStorage(key, dflt) {
    let store;
    if (isUndefined(key))
        return dflt ?? {};
    switch (context.type) {
        case CONTEXT.Browser:
            store = storage.getItem(key);
            break;
        case CONTEXT.NodeJS:
            store = nodeStorage.has(key) ? nodeStorage.get(key) : getEnvVar(key);
            break;
        case CONTEXT.Deno:
            store = context.global.Deno.env.get(key);
            break;
        case CONTEXT.GoogleAppsScript:
            store = context.global.PropertiesService?.getUserProperties().getProperty(key);
            break;
        case CONTEXT.WebWorker:
        case CONTEXT.Unknown:
            store = undefined;
            break;
        default:
            throw new Error(`Cannot determine Javascript context: ${context.type}`);
    }
    return isString(store)
        ? objectify(store) // rebuild object from its stringified representation
        : dflt;
}
export function setStorage(key, val, options) {
    const stash = isDefined(val) ? stringify(val) : undefined;
    const set = isDefined(stash);
    switch (context.type) {
        case CONTEXT.Browser:
            try {
                set
                    ? storage.setItem(key, stash)
                    : storage.removeItem(key);
            }
            catch (e) {
                console.warn(`[Storage] Failed to ${set ? 'setItem' : 'removeItem'} for key '${key}':`, e);
            }
            break;
        case CONTEXT.NodeJS:
            if (!set) {
                nodeStorage.set(key, undefined, options?.ttl);
            }
            else if (options?.static) {
                nodeStorage.setStatic(key, stash);
            }
            else {
                nodeStorage.set(key, stash, options?.ttl);
            }
            break;
        case CONTEXT.Deno:
            set
                ? context.global.Deno.env.set(key, stash)
                : context.global.Deno.env.delete(key);
            break;
        case CONTEXT.GoogleAppsScript:
            set
                ? context.global.PropertiesService?.getUserProperties().setProperty(key, stash)
                : context.global.PropertiesService?.getUserProperties().deleteProperty(key);
            break;
        case CONTEXT.WebWorker:
        case CONTEXT.Unknown:
            break;
        default:
            throw new Error(`Cannot determine Javascript context: ${context.type}`);
    }
}
/**
 * Clears in-memory storage entries across environments (NodeJS nodeStorage, Browser memory storage).
 */
export function clearStorage() {
    switch (context.type) {
        case CONTEXT.Browser:
            try {
                for (const store of memoryStores.values())
                    store.clear();
            }
            catch {
                // ignore
            }
            break;
        case CONTEXT.NodeJS:
            nodeStorage.clear();
            storage.clear();
            break;
        default:
            storage.clear();
            break;
    }
}
/**
 * Safely determines if the current execution context is in a unit-testing environment (e.g. Vitest, Jest, or CI).
 * Uses universal getStorage API to avoid direct environment variable AST inspection.
 *
 * @returns True if running in a test suite or CI environment
 */
export const isTestEnvironment = () => {
    return Boolean(context.global.VITEST ||
        context.global.__VITEST_ENVIRONMENT__ ||
        getStorage('TEMPO_TESTING', false) ||
        getStorage('NODE_ENV', '') === 'test' ||
        getStorage('CI', false));
};
